import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { LeagueModel } from '@/models/LeagueModels';

interface HandicapDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    handicap: number | null;
    handicapCalcSteps: string[] | null;
    courseName?: string;
    dayId?: string;
    flightId?: string | null;
    league?: LeagueModel;
}

const HandicapDetailsModal: React.FC<HandicapDetailsModalProps> = ({
    isOpen,
    onClose,
    handicap,
    handicapCalcSteps,
    courseName,
    dayId,
    flightId,
    league
}) => {
    const { theme } = useTheme();

    // Function to get flight name from flightId
    const getFlightName = (flightId: string | null): string => {
        if (!flightId || !league) return 'N/A';
        const flight = league.flights.find(f => f.id === flightId);
        return flight ? flight.name : 'N/A';
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: theme.spacing.MEDIUM
        }}>
            <div style={{
                backgroundColor: theme.colors.BACKGROUND,
                borderRadius: theme.borderRadius.MEDIUM,
                padding: theme.spacing.LARGE,
                maxWidth: '500px',
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                border: `1px solid ${theme.colors.BORDER}`
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing.LARGE,
                    borderBottom: `1px solid ${theme.colors.BORDER}`,
                    paddingBottom: theme.spacing.MEDIUM
                }}>
                    <h2 style={{
                        color: theme.colors.TEXT,
                        margin: 0,
                        fontSize: '1.5em',
                        fontWeight: 600
                    }}>
                        Handicap Details
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5em',
                            cursor: 'pointer',
                            color: theme.colors.TEXT_SECONDARY,
                            padding: '4px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px'
                        }}
                        title="Close"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div style={{ marginBottom: theme.spacing.LARGE }}>
                    {/* Combined Round Info */}
                    <div style={{
                        padding: theme.spacing.MEDIUM,
                        backgroundColor: theme.colors.SURFACE,
                        borderRadius: theme.borderRadius.SMALL,
                        border: `1px solid ${theme.colors.BORDER}`,
                        marginBottom: theme.spacing.MEDIUM
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: theme.spacing.MEDIUM
                        }}>
                            {/* Handicap */}
                            <div>
                                <div style={{
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontSize: '0.9em',
                                    marginBottom: '4px'
                                }}>
                                    Handicap
                                </div>
                                <div style={{
                                    color: theme.colors.TEXT,
                                    fontSize: '1.8em',
                                    fontWeight: 'bold'
                                }}>
                                    {handicap !== null ? handicap : 'N/A'}
                                </div>
                            </div>

                            {/* Flight */}
                            <div>
                                <div style={{
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontSize: '0.9em',
                                    marginBottom: '4px'
                                }}>
                                    Flight
                                </div>
                                <div style={{
                                    color: theme.colors.TEXT,
                                    fontSize: '1.2em',
                                    fontWeight: 600
                                }}>
                                    {getFlightName(flightId || null)}
                                </div>
                            </div>

                            {/* Course */}
                            <div>
                                <div style={{
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontSize: '0.9em',
                                    marginBottom: '4px'
                                }}>
                                    Course
                                </div>
                                <div style={{
                                    color: theme.colors.TEXT,
                                    fontSize: '1em',
                                    fontWeight: 500
                                }}>
                                    {courseName || 'N/A'}
                                </div>
                            </div>

                            {/* Date */}
                            <div>
                                <div style={{
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontSize: '0.9em',
                                    marginBottom: '4px'
                                }}>
                                    Round Date
                                </div>
                                <div style={{
                                    color: theme.colors.TEXT,
                                    fontSize: '1em',
                                    fontWeight: 500
                                }}>
                                    {dayId ? new Date(dayId.replace('day_', '').replace(/(\d{4})_(\d{2})_(\d{2})/, '$1-$2-$3T00:00:00')).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric'
                                    }) : 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Calculation Steps */}
                    <div style={{ marginBottom: theme.spacing.MEDIUM }}>
                        <h3 style={{
                            color: theme.colors.TEXT,
                            margin: '0 0 12px 0',
                            fontSize: '1.2em',
                            fontWeight: 600
                        }}>
                            Calculation Steps
                        </h3>

                        {handicapCalcSteps && handicapCalcSteps.length > 0 ? (
                            <div style={{
                                backgroundColor: theme.colors.SURFACE,
                                borderRadius: theme.borderRadius.SMALL,
                                border: `1px solid ${theme.colors.BORDER}`,
                                overflow: 'hidden'
                            }}>
                                {handicapCalcSteps.map((step, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            padding: theme.spacing.MEDIUM,
                                            borderBottom: index < handicapCalcSteps.length - 1
                                                ? `1px solid ${theme.colors.BORDER}`
                                                : 'none',
                                            backgroundColor: index % 2 === 0
                                                ? theme.colors.SURFACE
                                                : theme.colors.BACKGROUND
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: theme.spacing.SMALL
                                        }}>
                                            <div style={{
                                                backgroundColor: theme.colors.PRIMARY,
                                                color: theme.colors.WHITE,
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.8em',
                                                fontWeight: 'bold',
                                                flexShrink: 0,
                                                marginTop: '2px'
                                            }}>
                                                {index + 1}
                                            </div>
                                            <div style={{
                                                color: theme.colors.TEXT,
                                                fontSize: '0.95em',
                                                lineHeight: '1.4',
                                                flex: 1
                                            }}>
                                                {step}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                padding: theme.spacing.MEDIUM,
                                backgroundColor: theme.colors.SURFACE,
                                borderRadius: theme.borderRadius.SMALL,
                                border: `1px solid ${theme.colors.BORDER}`,
                                textAlign: 'center',
                                color: theme.colors.TEXT_SECONDARY,
                                fontStyle: 'italic'
                            }}>
                                Calculation steps are unavailable until after week is locked
                            </div>
                        )}
                    </div>

                    {/* Handicap Info */}
                    <div style={{
                        padding: theme.spacing.MEDIUM,
                        backgroundColor: theme.colors.SURFACE,
                        borderRadius: theme.borderRadius.SMALL,
                        border: `1px solid ${theme.colors.BORDER}`
                    }}>
                        <h4 style={{
                            color: theme.colors.TEXT,
                            margin: '0 0 8px 0',
                            fontSize: '1em',
                            fontWeight: 600
                        }}>
                            About Handicaps
                        </h4>
                        <div style={{
                            color: theme.colors.TEXT_SECONDARY,
                            fontSize: '0.9em',
                            lineHeight: '1.4'
                        }}>
                            <p style={{ marginBottom: '4px' }}>
                                To ensure fair competition, handicaps are re-calculated only at the end of a skins session. A skins session lasts for 18 holes, or two weeks of play.
                            </p>
                            <p style={{ marginBottom: '4px' }}>
                                Handicap calculation is based on the most recent <strong>6</strong> rounds of play. The high and low rounds are dropped, and the average of the remaining <strong>4</strong> rounds is used to calculate the handicap.
                            </p>
                            <p style={{ marginBottom: '4px' }}>
                                If less than 4 rounds of play are available, the handicap is not changed.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    borderTop: `1px solid ${theme.colors.BORDER}`,
                    paddingTop: theme.spacing.MEDIUM
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            background: theme.colors.PRIMARY,
                            color: theme.colors.WHITE,
                            border: 'none',
                            borderRadius: theme.borderRadius.SMALL,
                            padding: '12px 24px',
                            fontSize: '1em',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HandicapDetailsModal; 